import { Action } from 'redux';
import { PlansStateStructure } from '../interfaces/plansStateStructure.interface';
import { PlanCollection, PlanTypesCollection } from '../../interfaces/plan-types-collection.interface';
import { PRODUCT_GROUPS, PRODUCT_GROUP_NAMES } from 'src/app/angular-elements/plan-selection/enums/product-groups.enum';
import { MedicareAdvantage_and_PDP_Product } from '../../interfaces/product.interface';
import { ProductGroupInfo } from '../../interfaces/get-product-types.interfaces';
import { MedigapFormSubmit } from '../interfaces/medigap-form-submitted.interface';
import { RecommendedPlans } from '../../interfaces/recommended-plans.interface';

export const PLAN_STATE_NAME = 'Plans';
const INITIAL_STATE: PlansStateStructure = {
  plansByPlanType: null,
  medicareAdvantage_or_PDP_planForDRX: null,
  productGroupsForCounty: null,
  activeProductGroup: null,
  planIdForEnrollment: null,
  planNameForEnrollment: null,
  medigapValuesPostFormSubmit: {
    gender: null,
    macra: null,
    tobaccoUsage: null,
    dob: null,
    coverageDate: null,
    whb: null,
    hardStops: 'true',
    flow: null
  },
  plansNeedRefreshedFromBackend: false,
  recommendPlansFlowUsageCode: 1, // TODO: Make this come from an environment variable so we can disable this.
  recommendedPlanIds: null,
  isPlanChange: false
};

const SAVE_PLANS = 'SAVE_PLANS';
const SAVE_SINGLE_PLAN = 'SAVE_SINGLE_PLAN';
const INIT_PLANS = 'INIT_PLANS';
const SELECT_PLAN_FOR_DRX = 'SELECT_PLAN_FOR_DRX';
const SAVE_PRODUCT_GROUPS_FOR_COUNTY = 'SAVE_PRODUCT_GROUPS_FOR_COUNTY';
const SET_ACTIVE_PRODUCT_GROUP = 'SET_ACTIVE_PRODUCT_GROUP';
const SAVE_PLAN_ID_FOR_ENROLLMENT = 'SAVE_PLAN_ID_FOR_ENROLLMENT';
const SAVE_PLAN_NAME_FOR_ENROLLMENT = 'SAVE_PLAN_NAME_FOR_ENROLLMENT';
const SAVE_MEDIGAP_VALUES_POST_FORM_SUBMIT = 'SAVE_MEDIGAP_VALUES_POST_FORM_SUBMIT';
const ZIP_CHANGED_RESET_PLANS = 'ZIP_CHANGED_RESET_PLANS';
const DISCARD_EXISTING_MEDIGAP_PRODUCTS = 'DISCARD_EXISTING_MEDIGAP_PRODUCTS';
const PLANS_NEED_REFRESHED_FROM_BACKEND = 'PLANS_NEED_REFRESHED_FROM_BACKEND';
const SET_USAGE_CODE = 'SET_USAGE_CODE';
const SET_RECOMMENDED_PLAN_IDS = 'SET_RECOMMENDED_PLAN_IDS';
const SET_HARD_STOPS = 'SET_HARD_STOPS';
const SET_PLAN_CHANGE = 'SET_PLAN_CHANGE';
const HOTSWAP_STATE = `${PLAN_STATE_NAME}_HOTSWAP_STATE`;

export class PlansAction implements Action {
  type: string;
  plansByPlanType?: PlanTypesCollection;
  medicareAdvantage_or_PDP_planForDRX?: MedicareAdvantage_and_PDP_Product;
  productGroupsForCounty?: ProductGroupInfo;
  activeProductGroup?: PRODUCT_GROUPS;
  planIdForEnrollment?: string;
  planNameForEnrollment?: string;
  medigapValuesPostFormSubmit?: MedigapFormSubmit;
  plansNeedRefreshedFromBackend?: boolean;
  recommendPlansFlowUsageCode?: number;
  recommendedPlanIds?: RecommendedPlans;
  oldGroup?: string;
  newGroup?: string;
  hardStops?: string;
  whbcrosswalk?: string;
  productGroup?: string;
  planDetails?: any;
  isPlanChange?: boolean;
  newState?: PlansStateStructure;
}

export function saveSinglePlan(productGroup: string, planDetails: any) {
  return { type: SAVE_SINGLE_PLAN, productGroup, planDetails};
}

export function savePlans(plansByPlanType: PlanTypesCollection) {
  return { type: SAVE_PLANS, plansByPlanType };
}

export function selectPlanForDRX(
  medicareAdvantage_or_PDP_planForDRX: MedicareAdvantage_and_PDP_Product
) {
  return { type: SELECT_PLAN_FOR_DRX, medicareAdvantage_or_PDP_planForDRX };
}

export function setActiveProductGroup(activeProductGroup: PRODUCT_GROUP_NAMES) {
  return { type: SET_ACTIVE_PRODUCT_GROUP, activeProductGroup };
}

export function saveProductGroupsForCounty(productGroupsForCounty: ProductGroupInfo) {
  return { type: SAVE_PRODUCT_GROUPS_FOR_COUNTY, productGroupsForCounty };
}

export function initializePlans() {
  return { type: INIT_PLANS };
}

export function savePlanIdForEnrollment(planIdForEnrollment: string): PlansAction {
  return { type: SAVE_PLAN_ID_FOR_ENROLLMENT, planIdForEnrollment };
}

export function savePlanNameForEnrollment(planNameForEnrollment: string): PlansAction {
  return { type: SAVE_PLAN_NAME_FOR_ENROLLMENT, planNameForEnrollment };
}

export function saveMedigapValuesPostFormSubmit(medigapValuesPostFormSubmit: MedigapFormSubmit): PlansAction {
  return { type: SAVE_MEDIGAP_VALUES_POST_FORM_SUBMIT, medigapValuesPostFormSubmit };
}

export function setHardStops(hardStops: string): PlansAction {
  return { type: SET_HARD_STOPS, hardStops };
}

export function plansNeedRefreshedFromBackend(doPlansNeedRefreshedFromBackend: boolean) {
  return { type: PLANS_NEED_REFRESHED_FROM_BACKEND, plansNeedRefreshedFromBackend: doPlansNeedRefreshedFromBackend };
}

/** Optionally pass true to trigger medigap form reset
 * @TODO - consider moving the medigap form stuff into a separate reducer
 */
export function zipChangedResetPlans(): PlansAction {
  return { type: ZIP_CHANGED_RESET_PLANS };
}

export function discardExistingMedigapProducts(): PlansAction {
  return { type: DISCARD_EXISTING_MEDIGAP_PRODUCTS };
}

export function setRecommendPlansFlowUsageCode(recommendPlansFlowUsageCode: number) {
  return { type: SET_USAGE_CODE, recommendPlansFlowUsageCode };
}

export function setRecommendedPlanIds(recommendedPlanIds: RecommendedPlans) {
  return { type: SET_RECOMMENDED_PLAN_IDS, recommendedPlanIds };
}

export function setPlanChange(isPlanChange: boolean) {
  return { type: SET_PLAN_CHANGE, isPlanChange };
}

export function hotswapPlansState(newState: PlansStateStructure): PlansAction {
  return { type: HOTSWAP_STATE, newState };
}

export function Plans(
  state: PlansStateStructure = INITIAL_STATE,
  action: PlansAction
) {
  const copiedState: PlansStateStructure = JSON.parse(JSON.stringify(Object.assign({}, state)));

  switch (action.type) {
    /** Save Plans From Service Response */
    case SAVE_PLANS:
      copiedState.plansByPlanType = action.plansByPlanType;
      return Object.assign({}, state, copiedState);
    case SAVE_SINGLE_PLAN:
      if (!copiedState.plansByPlanType) {
        copiedState.plansByPlanType = {} as PlanTypesCollection;
      }
      if (!copiedState.plansByPlanType[action.productGroup]) {
        copiedState.plansByPlanType[action.productGroup] = {};
      }
      copiedState.plansByPlanType[action.productGroup][action.planDetails.productId] = action.planDetails;
      return Object.assign({}, state, copiedState);
    case SELECT_PLAN_FOR_DRX:
      copiedState.medicareAdvantage_or_PDP_planForDRX =
        action.medicareAdvantage_or_PDP_planForDRX;
      return Object.assign({}, state, copiedState);
    case SET_ACTIVE_PRODUCT_GROUP:
      copiedState.activeProductGroup = action.activeProductGroup;
      return Object.assign({}, state, copiedState);
    case SAVE_PRODUCT_GROUPS_FOR_COUNTY:
      copiedState.productGroupsForCounty = action.productGroupsForCounty;
      return Object.assign({}, state, copiedState);
    case SAVE_PLAN_ID_FOR_ENROLLMENT:
      copiedState.planIdForEnrollment = action.planIdForEnrollment;
      return Object.assign({}, state, copiedState);
    case SAVE_PLAN_NAME_FOR_ENROLLMENT:
      copiedState.planNameForEnrollment = action.planNameForEnrollment;
      return Object.assign({}, state, copiedState);
    case SAVE_MEDIGAP_VALUES_POST_FORM_SUBMIT:
      copiedState.medigapValuesPostFormSubmit = action.medigapValuesPostFormSubmit;
      return Object.assign({}, state, copiedState);

    case SET_HARD_STOPS:
      if (copiedState.medigapValuesPostFormSubmit) {
        copiedState.medigapValuesPostFormSubmit.hardStops = action.hardStops;
      }
      return Object.assign({}, state, copiedState);
    case ZIP_CHANGED_RESET_PLANS:
      const initialStateCopy: PlansStateStructure = JSON.parse(JSON.stringify(INITIAL_STATE));
      /**
       * Maintain the active product group - this would reset it during the WHB flow, it's fine to maintain here as we check in the tabs
       * if it's safe to use anyway
       */
      initialStateCopy.activeProductGroup = state.activeProductGroup;
      initialStateCopy.isPlanChange = copiedState.isPlanChange;

      /** DON'T LOSE THE MEDIGAP VALUES!!! (not a huge deal, but requirement to not remove added data) */
      initialStateCopy.medigapValuesPostFormSubmit = copiedState.medigapValuesPostFormSubmit;

      /** We DO want to forget the 'flow' value because eligibility for medigap is now pre-plans, but only for some regions */
      initialStateCopy.medigapValuesPostFormSubmit.flow = null;

      return Object.assign({}, state, initialStateCopy);
    case PLANS_NEED_REFRESHED_FROM_BACKEND:
      copiedState.plansNeedRefreshedFromBackend = action.plansNeedRefreshedFromBackend;
      return Object.assign({}, state, copiedState);
    case DISCARD_EXISTING_MEDIGAP_PRODUCTS:
      if (copiedState.plansByPlanType.Medigap) {
        copiedState.plansByPlanType.Medigap = undefined;
      }
      if (copiedState.plansByPlanType['Medicare Supplement']) {
        copiedState.plansByPlanType['Medicare Supplement'] = undefined;
      }

      return Object.assign({}, state, copiedState);
    case SET_USAGE_CODE:
      copiedState.recommendPlansFlowUsageCode = action.recommendPlansFlowUsageCode;
      return Object.assign({}, state, copiedState);
    case SET_RECOMMENDED_PLAN_IDS:
      copiedState.recommendedPlanIds = action.recommendedPlanIds;
      return Object.assign({}, state, copiedState);
    case SET_PLAN_CHANGE:
      copiedState.isPlanChange = action.isPlanChange;
      return Object.assign({}, state, copiedState);
    case HOTSWAP_STATE:
      // Replace the entire state with the new state
      return action.newState;
    default:
      return state;
  }
}
